/**
 * PHASE 4: DATABASE CLEANUP AND CONSISTENCY
 * Profile name parsing and migration utilities
 */

import { backendClient } from '@/lib/backendClient';
import { parseFullName } from './nameParser';
import { validatePersonalizationName, sanitizeProfileData } from './nameContaminationBlocker';

export interface ProfileMigrationResult {
  success: boolean;
  processed: number;
  repaired: number;
  errors: string[];
  warnings: string[];
}

export interface NameParsingResult {
  firstName: string;
  lastName?: string;
  needsUpdate: boolean;
  contaminated: boolean;
}

/**
 * Parse and validate a full name for profile migration
 */
export const parseAndValidateProfileName = (fullName: string): NameParsingResult => {
  if (!fullName?.trim()) {
    return {
      firstName: '',
      needsUpdate: false,
      contaminated: false
    };
  }

  const parsed = parseFullName(fullName);
  
  // Validate for contamination
  const firstNameValidation = validatePersonalizationName(parsed.firstName);
  const lastNameValidation = parsed.lastName ? validatePersonalizationName(parsed.lastName) : { isClean: true };
  
  const contaminated = !firstNameValidation.isClean || !lastNameValidation.isClean;
  
  if (contaminated) {
    console.error('🚨 [MIGRATION] Contaminated name detected in full_name:', fullName, {
      firstName: parsed.firstName,
      lastName: parsed.lastName,
      firstNameClean: firstNameValidation.isClean,
      lastNameClean: lastNameValidation.isClean
    });
  }

  return {
    firstName: contaminated ? '' : parsed.firstName,
    lastName: contaminated ? undefined : parsed.lastName,
    needsUpdate: !contaminated && !!parsed.firstName,
    contaminated
  };
};

/**
 * Migrate a single user profile to split full names
 */
export const migrateUserProfile = async (userId: string): Promise<ProfileMigrationResult> => {
  const errors: string[] = [];
  const warnings: string[] = [];
  let repaired = 0;

  try {
    console.log('🔄 [MIGRATION] Starting profile migration for user:', userId);

    // Fetch current profile
    const { data: currentProfile, error: fetchError } = await backendClient
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (fetchError) {
      errors.push(`Failed to fetch profile: ${fetchError.message}`);
      return { success: false, processed: 0, repaired: 0, errors, warnings };
    }

    if (!currentProfile) {
      warnings.push('No profile found for user');
      return { success: true, processed: 0, repaired: 0, errors, warnings };
    }

    console.log('📋 [MIGRATION] Current profile state:', {
      hasFullName: !!currentProfile.full_name,
      hasFirstName: !!currentProfile.first_name,
      hasLastName: !!currentProfile.last_name,
      fullName: currentProfile.full_name,
      firstName: currentProfile.first_name,
      lastName: currentProfile.last_name
    });

    // Check if migration is needed
    const needsMigration = currentProfile.full_name && 
                          (!currentProfile.first_name || !currentProfile.last_name);

    if (!needsMigration) {
      console.log('✅ [MIGRATION] Profile does not need migration');
      return { success: true, processed: 1, repaired: 0, errors, warnings };
    }

    // Parse the full name
    const parseResult = parseAndValidateProfileName(currentProfile.full_name);

    if (parseResult.contaminated) {
      errors.push('Profile contains contaminated names - migration blocked');
      return { success: false, processed: 1, repaired: 0, errors, warnings };
    }

    if (!parseResult.needsUpdate) {
      warnings.push('Full name could not be parsed into valid components');
      return { success: true, processed: 1, repaired: 0, errors, warnings };
    }

    // Prepare update data
    const updateData: any = {
      first_name: currentProfile.first_name || parseResult.firstName,
      updated_at: new Date().toISOString()
    };

    if (parseResult.lastName) {
      updateData.last_name = currentProfile.last_name || parseResult.lastName;
    }

    // Ensure name usage preference is set
    if (!currentProfile.name_usage_preference) {
      updateData.name_usage_preference = 'first';
    }

    console.log('🔄 [MIGRATION] Applying updates:', updateData);

    // Apply the migration
    const { error: updateError } = await backendClient
      .from('profiles')
      .update(updateData)
      .eq('id', userId);

    if (updateError) {
      errors.push(`Failed to update profile: ${updateError.message}`);
      return { success: false, processed: 1, repaired: 0, errors, warnings };
    }

    repaired = 1;
    console.log('✅ [MIGRATION] Profile migration completed successfully');

    return { success: true, processed: 1, repaired, errors, warnings };

  } catch (error: any) {
    errors.push(`Migration failed: ${error.message}`);
    return { success: false, processed: 0, repaired: 0, errors, warnings };
  }
};

/**
 * Batch migrate all user profiles (admin function)
 */
export const migrateAllProfiles = async (): Promise<ProfileMigrationResult> => {
  const overallResult: ProfileMigrationResult = {
    success: true,
    processed: 0,
    repaired: 0,
    errors: [],
    warnings: []
  };

  try {
    console.log('🚀 [BATCH MIGRATION] Starting batch profile migration...');

    // Fetch all profiles that need migration
    const { data: profiles, error: fetchError } = await backendClient
      .from('profiles')
      .select('id, full_name, first_name, last_name')
      .not('full_name', 'is', null)
      .limit(100); // Process in batches

    if (fetchError) {
      overallResult.errors.push(`Failed to fetch profiles: ${fetchError.message}`);
      overallResult.success = false;
      return overallResult;
    }

    if (!profiles || profiles.length === 0) {
      console.log('✅ [BATCH MIGRATION] No profiles need migration');
      return overallResult;
    }

    console.log(`📊 [BATCH MIGRATION] Found ${profiles.length} profiles to check`);

    // Process each profile
    for (const profile of profiles) {
      const result = await migrateUserProfile(profile.id);
      
      overallResult.processed += result.processed;
      overallResult.repaired += result.repaired;
      overallResult.errors.push(...result.errors);
      overallResult.warnings.push(...result.warnings);
      
      if (!result.success) {
        overallResult.success = false;
      }

      // Small delay to avoid overwhelming the database
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    console.log('🎉 [BATCH MIGRATION] Batch migration completed:', overallResult);
    return overallResult;

  } catch (error: any) {
    overallResult.errors.push(`Batch migration failed: ${error.message}`);
    overallResult.success = false;
    return overallResult;
  }
};

/**
 * Validate and repair a user's current profile data
 */
export const validateAndRepairCurrentProfile = async (userId: string): Promise<{
  isValid: boolean;
  repaired: boolean;
  issues: string[];
  repairedData?: any;
}> => {
  try {
    const { data: profile, error } = await backendClient
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error || !profile) {
      return {
        isValid: false,
        repaired: false,
        issues: ['Profile not found or cannot be accessed']
      };
    }

    const issues: string[] = [];
    let needsRepair = false;
    const repairedData = { ...profile };

    // Check for contaminated names
    const cleanedProfile = sanitizeProfileData(profile);
    if (JSON.stringify(cleanedProfile) !== JSON.stringify(profile)) {
      issues.push('Profile contains contaminated names');
      Object.assign(repairedData, cleanedProfile);
      needsRepair = true;
    }

    // Check if full name needs parsing
    if (profile.full_name && (!profile.first_name || !profile.last_name)) {
      const parseResult = parseAndValidateProfileName(profile.full_name);
      if (parseResult.needsUpdate && !parseResult.contaminated) {
        repairedData.first_name = repairedData.first_name || parseResult.firstName;
        if (parseResult.lastName) {
          repairedData.last_name = repairedData.last_name || parseResult.lastName;
        }
        issues.push('Full name was parsed into first and last name');
        needsRepair = true;
      }
    }

    // Set default name usage preference
    if (!profile.name_usage_preference) {
      repairedData.name_usage_preference = 'first';
      issues.push('Set default name usage preference');
      needsRepair = true;
    }

    if (needsRepair) {
      repairedData.updated_at = new Date().toISOString();
      
      const { error: updateError } = await backendClient
        .from('profiles')
        .update(repairedData)
        .eq('id', userId);

      if (updateError) {
        return {
          isValid: false,
          repaired: false,
          issues: [...issues, `Failed to repair profile: ${updateError.message}`]
        };
      }
    }

    return {
      isValid: issues.length === 0,
      repaired: needsRepair,
      issues,
      repairedData: needsRepair ? repairedData : undefined
    };

  } catch (error: any) {
    return {
      isValid: false,
      repaired: false,
      issues: [`Profile validation failed: ${error.message}`]
    };
  }
};
